/**
 * Platform Core Integration
 *
 * Optional integration with @digilogiclabs/platform-core for:
 * - Logging (payment events, webhook processing)
 * - Metrics (payment success/failure tracking, revenue metrics)
 * - Cache (subscription status caching)
 */

import type { ILogger, IMetrics, ICache } from '@digilogiclabs/platform-core';

interface PlatformServices {
  logger?: ILogger;
  metrics?: IMetrics;
  cache?: ICache;
}

let platformServices: PlatformServices = {};

/**
 * Set the platform services for payments package to use
 *
 * @example
 * ```typescript
 * import { createPlatform } from '@digilogiclabs/platform-core';
 * import { setPlatform } from '@digilogiclabs/saas-factory-payments';
 *
 * const platform = createPlatform();
 * setPlatform({
 *   logger: platform.logger,
 *   metrics: platform.metrics,
 *   cache: platform.cache,
 * });
 * ```
 */
export function setPlatform(services: PlatformServices): void {
  platformServices = { ...platformServices, ...services };
}

/**
 * Clear platform services (useful for testing)
 */
export function clearPlatform(): void {
  platformServices = {};
}

/**
 * Get the configured logger
 */
export function getLogger(): ILogger | undefined {
  return platformServices.logger;
}

/**
 * Get the configured metrics
 */
export function getMetrics(): IMetrics | undefined {
  return platformServices.metrics;
}

/**
 * Get the configured cache
 */
export function getCache(): ICache | undefined {
  return platformServices.cache;
}

/**
 * Check if platform services are configured
 */
export function hasPlatform(): boolean {
  return Object.keys(platformServices).length > 0;
}

/**
 * Log a payment event if logger is configured
 */
export function logPaymentEvent(
  level: 'debug' | 'info' | 'warn' | 'error',
  message: string,
  meta?: Record<string, unknown>
): void {
  const logger = platformServices.logger;
  if (logger) {
    logger[level](message, meta);
  }
}

/**
 * Track a payment metric if metrics are configured
 */
export function trackPaymentMetric(
  name: string,
  value: number = 1,
  tags?: Record<string, string | number | boolean>
): void {
  const metrics = platformServices.metrics;
  if (metrics) {
    metrics.increment(`payments.${name}`, value, tags);
  }
}

/**
 * Track payment revenue
 */
export function trackRevenue(
  amount: number,
  currency: string,
  tags?: Record<string, string | number | boolean>
): void {
  const metrics = platformServices.metrics;
  if (metrics) {
    metrics.gauge('payments.revenue', amount, { currency, ...tags });
  }
}

/**
 * Cache subscription status
 */
export async function cacheSubscriptionStatus(
  customerId: string,
  status: string,
  ttl: number = 300
): Promise<void> {
  const cache = platformServices.cache;
  if (cache) {
    try {
      await cache.set(`subscription:${customerId}`, status, ttl);
      logPaymentEvent('debug', 'Cached subscription status', {
        customerId,
        status,
      });
    } catch (error) {
      logPaymentEvent('warn', 'Failed to cache subscription status', {
        customerId,
        error,
      });
    }
  }
}

/**
 * Get cached subscription status
 */
export async function getCachedSubscriptionStatus(
  customerId: string
): Promise<string | null> {
  const cache = platformServices.cache;
  if (cache) {
    try {
      return await cache.get<string>(`subscription:${customerId}`);
    } catch (error) {
      logPaymentEvent('warn', 'Failed to get cached subscription status', {
        customerId,
        error,
      });
    }
  }
  return null;
}

/**
 * Track webhook processing
 */
export function trackWebhookEvent(
  eventType: string,
  success: boolean,
  duration?: number
): void {
  const metrics = platformServices.metrics;
  if (metrics) {
    metrics.increment(
      success ? 'payments.webhook.success' : 'payments.webhook.failure',
      1,
      { event_type: eventType }
    );
    if (duration !== undefined) {
      metrics.timing('payments.webhook.duration', duration, {
        event_type: eventType,
      });
    }
  }
}

/**
 * Track checkout session
 */
export function trackCheckoutSession(
  success: boolean,
  tags?: Record<string, string | number | boolean>
): void {
  trackPaymentMetric(
    success ? 'checkout.success' : 'checkout.failure',
    1,
    tags
  );
}

/**
 * Track subscription events
 */
export function trackSubscriptionEvent(
  event: 'created' | 'updated' | 'canceled' | 'renewed',
  tags?: Record<string, string | number | boolean>
): void {
  trackPaymentMetric(`subscription.${event}`, 1, tags);
}
